import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  PartnerClientCreate,
  PartnerClientCreatedResponse,
  PartnerClientDetail,
  PartnerClientStorageResponse,
  PartnerClientStorageUpdate,
  PartnerClientSummary,
  PartnerCreditsAssignRequest,
  PartnerLimitRequest,
  PartnerLimitRequestCreate,
  PartnerImpersonateResponse,
  PartnerLoginRequest,
  PartnerLoginResponse,
  PartnerMe,
  PartnerPasswordChange,
  PartnerPurchaseRequest,
  PartnerPurchaseResponse,
  PartnerStatement,
  PartnerWholesaleEntry
} from '../types/partner';
import {
  AccountUser,
  AccountUserCheck,
  AccountUserCreateRequest,
  AccountUserCreatedResponse,
  EmployeeStorageRequest,
  EmployeeStorageResponse,
  EmployeeUpdate,
  PublicListingOption
} from '../types/clientAccount';
import { PartnerPackages } from '../types/creditPackage';
import {
  isPasswordChangeRequiredResponse,
  notifyPasswordChangeRequired,
  portalLoginErrorFromAxios
} from '../utils/portalAuthErrors';

// Cliente HTTP del PORTAL PARTNER (/api/v1/partner/*). Auth propia e
// independiente del admin y del cliente: token en localStorage `partner_token`.
// Los rechazos devuelven err.detail cuando el backend lo emite (409 sin saldo
// mayorista, 401, etc.) — misma convención que customerApi.

const PARTNER_TOKEN_KEY = 'partner_token';
export const PARTNER_ME_KEY = 'partner_me';

class PartnerApiService {
  private api: AxiosInstance;
  private authToken: string | null = null;

  constructor() {
    const baseURL = window.location.protocol === 'https:'
      ? `https://${window.location.host}/api/v1/partner`
      : '/api/v1/partner';

    this.api = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.authToken = localStorage.getItem(PARTNER_TOKEN_KEY);
    if (this.authToken) {
      this.setAuthToken(this.authToken);
    }

    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // El LOGIN es la excepción a las dos reglas de abajo:
        //  · un 401 suyo NO es sesión caducada (es una credencial mala), y
        //    recargar /partner/login se llevaba por delante el mensaje de error;
        //  · normalizar el error a `data` destruía status y cabeceras, que es
        //    donde viaja el contrato F1 (`X-Auth-Error-Code`, intentos
        //    restantes, minutos de bloqueo).
        const isLogin = (error.config?.url || '').includes('/auth/login');
        if (isLogin) {
          return Promise.reject(error);
        }
        // Veto en servidor por contraseña de un solo uso (403
        // PASSWORD_CHANGE_REQUIRED): sesión VÁLIDA — no se limpia el token ni
        // se redirige al login (sería un bucle). Se avisa al contexto para que
        // pinte el gate bloqueante.
        if (isPasswordChangeRequiredResponse(error.response)) {
          notifyPasswordChangeRequired('partner');
          return Promise.reject(error.response?.data || error.message);
        }
        // 401 = sesión partner caducada → limpiar y volver al login del portal
        // partner (solo si estamos dentro de /partner, no pisar otros portales).
        if (error.response?.status === 401) {
          this.clearAuth();
          if (window.location.pathname.startsWith('/partner')) {
            window.location.href = '/partner/login';
          }
        }
        return Promise.reject(error.response?.data || error.message);
      }
    );
  }

  setAuthToken(token: string) {
    this.authToken = token;
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem(PARTNER_TOKEN_KEY, token);
  }

  clearAuth() {
    this.authToken = null;
    delete this.api.defaults.headers.common['Authorization'];
    localStorage.removeItem(PARTNER_TOKEN_KEY);
    localStorage.removeItem(PARTNER_ME_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.authToken;
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  async login(credentials: PartnerLoginRequest): Promise<PartnerLoginResponse> {
    try {
      const response = await this.api.post<PartnerLoginResponse>('/auth/login', credentials);
      this.setAuthToken(response.data.access_token);
      return response.data;
    } catch (error) {
      // El interceptor deja pasar el AxiosError crudo para el login: aquí se
      // convierte en el error del contrato F1, con el mensaje ya traducido.
      throw portalLoginErrorFromAxios(error);
    }
  }

  async getMe(): Promise<PartnerMe> {
    const response = await this.api.get<PartnerMe>('/me');
    return response.data;
  }

  /** Salida del gate obligatorio del portal partner (contrato F1). */
  async changePassword(data: PartnerPasswordChange): Promise<{ message: string }> {
    const response = await this.api.post('/auth/change-password', data);
    return response.data;
  }

  /** Fija la TALLA por defecto de los empleados de su cartera: la que saldrá
   *  marcada al contratar. Es una preferencia, no un tope, y NO toca a ningún
   *  empleado ya desplegado — para eso hay que redesplegarlo (lo dice `detail`). */
  async setDefaultEmployeeSize(size: string): Promise<{
    default_employee_size: string; detail?: string;
  }> {
    const response = await this.api.put('/default-employee-size',
      { default_employee_size: size });
    return response.data;
  }

  // ── Cartera de clientes ──────────────────────────────────────────────────

  async getClients(): Promise<PartnerClientSummary[]> {
    const response = await this.api.get<PartnerClientSummary[]>('/clients');
    return response.data;
  }

  async createClient(data: PartnerClientCreate): Promise<PartnerClientCreatedResponse> {
    const response = await this.api.post<PartnerClientCreatedResponse>('/clients', data);
    return response.data;
  }

  // Detalle: mismo shape que el admin (ClientAccountDetail) + el almacenamiento
  // de la CARTERA (portfolio_storage), que es lo que el partner reparte.
  async getClientEmployeeUpdates(clientId: number): Promise<EmployeeUpdate[]> {
    const response = await this.api.get<EmployeeUpdate[]>(`/clients/${clientId}/employee-updates`);
    return response.data;
  }

  // Sube un empleado desplegado de un cliente de la cartera a la última versión del
  // marketplace (re-instala en el tenant). 409 si ya está al día / no desplegado.
  async upgradeClientEmployee(clientId: number, employeeId: number): Promise<{ latest_version: string }> {
    const response = await this.api.post(`/clients/${clientId}/employees/${employeeId}/upgrade`);
    return response.data;
  }

  async getClient(clientId: number): Promise<PartnerClientDetail> {
    const response = await this.api.get<PartnerClientDetail>(`/clients/${clientId}`);
    return response.data;
  }

  // El partner REPARTE su capacidad: fija el almacenamiento incluido y el techo
  // de uno de sus clientes. Puede hacerlo porque el disco de toda la cartera se
  // lo factura el marketplace A ÉL. 409 si se pasa de su techo agregado (el
  // detail trae cuánto le queda). La respuesta trae el bloque de la CARTERA ya
  // recalculado para repintar «repartes X de Y GiB» sin otra llamada.
  async updateClientStorage(
    clientId: number,
    data: PartnerClientStorageUpdate
  ): Promise<PartnerClientStorageResponse> {
    const response = await this.api.patch(`/clients/${clientId}`, data);
    return response.data;
  }

  // Mismo shape que la asignación admin. El backend valida el cupo (409).
  // admin_account_user_id (obligatorio en el backend, 409 con el motivo si
  // falta): usuario DEL CLIENTE que administrará el empleado y bajo cuya
  // identidad trabajará.
  async assignEmployee(
    clientId: number,
    data: {
      listing_slug: string;
      display_name?: string;
      admin_account_user_id?: number;
      // Volúmenes del empleado (GiB); sin ellos, los defaults del backend.
      workspace_gib?: number;
      data_gib?: number;
      // TALLA (capacidad); sin ella, la que el partner tenga por defecto.
      size?: string;
    }
  ): Promise<{ account_employee_id: number; status: string }> {
    const response = await this.api.post(`/clients/${clientId}/employees`, data);
    return response.data;
  }

  // AMPLÍA los volúmenes de un empleado de un cliente de la cartera (valores
  // TOTALES). El backend comprueba PRIMERO el techo agregado del partner: es su
  // disco y su factura, así que no se puede ampliar por la puerta del cliente.
  // `sync` dice qué pasó con el DISCO REAL (applied/deferred/failed): un 2xx no
  // significa que el volumen del cliente haya crecido.
  async expandEmployeeStorage(
    clientId: number,
    employeeId: number,
    data: EmployeeStorageRequest
  ): Promise<EmployeeStorageResponse> {
    const response = await this.api.post(
      `/clients/${clientId}/employees/${employeeId}/storage`, data);
    return response.data;
  }

  // Retira un empleado del cliente (mismo efecto que la baja self-service):
  // se retira su app del tenant y se LIBERA el cupo. Irreversible.
  async removeEmployee(
    clientId: number,
    employeeId: number
  ): Promise<{ removed: boolean; tenant_error: string | null }> {
    const response = await this.api.delete(`/clients/${clientId}/employees/${employeeId}`);
    return response.data;
  }

  // ── Usuarios del cliente (el partner los gestiona como el propio cliente) ──
  // Sin esto el partner no puede levantar empleados: un empleado exige un
  // usuario REAL del cliente como administrador. Paralelo a customerApi pero
  // scopeado a /clients/{id}/users.
  async getClientUsers(clientId: number): Promise<AccountUser[]> {
    const response = await this.api.get<AccountUser[]>(`/clients/${clientId}/users`);
    return response.data;
  }

  async checkClientUserEmail(clientId: number, email: string): Promise<AccountUserCheck> {
    try {
      const response = await this.api.get<AccountUserCheck>(`/clients/${clientId}/users/check`, {
        params: { email }
      });
      return response.data;
    } catch (err) {
      console.warn('users/check no disponible:', err);
      return { email, checked: false, exists: false, in_account: false };
    }
  }

  async addClientUser(
    clientId: number,
    data: AccountUserCreateRequest
  ): Promise<AccountUserCreatedResponse> {
    const response = await this.api.post<AccountUserCreatedResponse>(`/clients/${clientId}/users`, data);
    return response.data;
  }

  async assignClientUser(
    clientId: number,
    userId: number,
    data: { account_employee_id: number; role: 'member' | 'admin' }
  ): Promise<{ assignment_id: number; role: string }> {
    const response = await this.api.post(`/clients/${clientId}/users/${userId}/assign`, data);
    return response.data;
  }

  async unassignClientUser(
    clientId: number,
    userId: number,
    accountEmployeeId: number
  ): Promise<void> {
    await this.api.delete(`/clients/${clientId}/users/${userId}/assign/${accountEmployeeId}`);
  }

  // Créditos al cliente DESDE la bolsa mayorista del partner, en sus dos formas:
  //   mode='topup' (default) → RECARGA puntual, no caduca, se paga una vez.
  //   mode='plan'            → PLAN MENSUAL recurrente: caduca cada ciclo en el
  //                            cliente y cada renovación vuelve a salir de la
  //                            bolsa mayorista. amount=0 retira el plan.
  // 409 si no hay saldo mayorista para el primer ciclo (detail = mensaje).
  async assignCredits(
    clientId: number,
    data: PartnerCreditsAssignRequest
  ): Promise<{
    mode?: string;
    wholesale_balance?: number | string;
    client_balance?: number | string;
  }> {
    const response = await this.api.post(`/clients/${clientId}/credits`, data);
    return response.data;
  }

  // Datos de la pantalla de COMPRA del partner: su precio mayorista plano por
  // crédito y, solo como referencia, el PVP de WAI al cliente final. Sin coste
  // ni margen por paquete: compra a granel, no revende paquetes.
  async getPackages(): Promise<PartnerPackages> {
    const response = await this.api.get<PartnerPackages>('/packages');
    return response.data;
  }

  // Token de cliente solo-lectura (15 min) para «Ver como cliente».
  async impersonateClient(clientId: number): Promise<PartnerImpersonateResponse> {
    const response = await this.api.post<PartnerImpersonateResponse>(`/clients/${clientId}/impersonate`);
    return response.data;
  }

  // ── Extracto de la bolsa mayorista ───────────────────────────────────────
  // El statement es MENSUAL y agregado: dice «cuánto», no «cuál». Esto es el
  // movimiento a movimiento — la misma lista que el admin ve en su ficha.
  // `kind`: purchase | allocation | adjustment | storage (ausente = todo).

  async getTransactions(kind?: string, limit = 200): Promise<PartnerWholesaleEntry[]> {
    const response = await this.api.get<PartnerWholesaleEntry[]>('/transactions', {
      params: { ...(kind ? { kind } : {}), limit }
    });
    return response.data;
  }

  // ── Compra de créditos (self-service, hasta su techo) ────────────────────
  // No hay aprobación por compra: el techo del partner es su línea de crédito
  // y compra libremente mientras el saldo resultante quepa ahí. 409 con detail
  // si se pasa — y el mensaje dice cuánto puede comprar aún o que pida
  // ampliación de techo.

  async purchaseCredits(data: PartnerPurchaseRequest): Promise<PartnerPurchaseResponse> {
    const response = await this.api.post<PartnerPurchaseResponse>('/purchases', data);
    return response.data;
  }

  // ── Solicitudes de AMPLIACIÓN DEL TECHO (pide el partner, aprueba el admin) ──

  async getLimitRequests(status?: string): Promise<PartnerLimitRequest[]> {
    const response = await this.api.get<PartnerLimitRequest[]>('/limit-requests', {
      params: status ? { status } : undefined
    });
    return response.data;
  }

  // Pedir no mueve techo ni saldo: queda pendiente hasta que el marketplace
  // decide. 409 si no tiene techo (ya compra sin límite) o si el techo pedido
  // no supera al actual.
  async createLimitRequest(data: PartnerLimitRequestCreate): Promise<PartnerLimitRequest> {
    const response = await this.api.post<PartnerLimitRequest>('/limit-requests', data);
    return response.data;
  }

  // Retirar una solicitud PENDIENTE propia (queda `cancelled`, no se borra).
  async cancelLimitRequest(requestId: number): Promise<PartnerLimitRequest> {
    const response = await this.api.post<PartnerLimitRequest>(
      `/limit-requests/${requestId}/cancel`);
    return response.data;
  }

  // ── Facturación (statement mensual, sin pasarela) ────────────────────────

  async getStatement(year: number, month: number): Promise<PartnerStatement> {
    const response = await this.api.get<PartnerStatement>('/statement', {
      params: { year, month }
    });
    return response.data;
  }

  // ── Catálogo público de empleados IA ─────────────────────────────────────
  // /public/agent-employees es público: llamada directa fuera del baseURL
  // partner para no arrastrar interceptores ni auth de otros portales.
  async getPublicListingOptions(): Promise<PublicListingOption[]> {
    const base = window.location.protocol === 'https:'
      ? `https://${window.location.host}/api/v1`
      : '/api/v1';
    const response = await axios.get<{ items: PublicListingOption[] }>(`${base}/public/agent-employees`, {
      params: { limit: 100 },
      timeout: 30000
    });
    return response.data.items || [];
  }
}

export const partnerApiService = new PartnerApiService();
export default partnerApiService;
